import CustomTransferList from 'Components/CustomTransferList/CustomTransferList'
import { WithLoader } from 'Components/WithLoader'
import { useUpdateSingleCatalogMutation } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import type { Catalog } from 'Services/ProductManagement/Features/Catalog/types'
import useTariffCatalogProducts from 'Services/ProductManagement/Features/Product/Hooks/useTariffCatalogProducts'
import useTariffProducts from 'Services/ProductManagement/Features/Product/Hooks/useTariffProducts'
import type { Product } from 'Services/ProductManagement/Features/Product/productSlice'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import ListItemText from '@mui/material/ListItemText'
import type * as React from 'react'
import { useId, useState } from 'react'
import { t } from 'ttag'

interface CatalogProductsEditProps {
  singleTariff: Tariff
  selectedCatalog: Catalog
  productGroupOptions: { label: string; value: string }[] | undefined
  handleCancel: () => void
  handleSaveSuccess?: () => void
}

function CatalogProductsEdit({
  singleTariff,
  selectedCatalog,
  productGroupOptions,
  handleCancel,
  handleSaveSuccess
}: Readonly<CatalogProductsEditProps>) {
  const { allTariffProducts, allTariffProductsLoading } = useTariffProducts(singleTariff.id)
  const [updateSingleCatalog, { isLoading: isUpdateLoading }] = useUpdateSingleCatalogMutation()
  const { allTariffCatalogProducts, allTariffCatalogProductsLoading } = useTariffCatalogProducts(
    singleTariff.id,
    selectedCatalog.id
  )

  const tariffProductsWithoutCatalogProducts = allTariffProducts.filter(
    product => !allTariffCatalogProducts.map(product => product.id).includes(product.id)
  )

  const [searchText, setSearchText] = useState('')
  const [productGroupFilter, setProductGroupFilter] = useState('')

  const filterOptions = (optionsToFilter: Product[]) => {
    const optionsFilteredBySearch = filterBySearch(optionsToFilter)
    const optionsFilteredByProductGroup = filterByProductGroup(optionsFilteredBySearch)
    return optionsFilteredByProductGroup
  }

  /**
   * Filters the given array of products based on the search text.
   *
   * The function checks if the search text is present in either the product name's finnish translation
   * or the product code. If the search text is not provided, it returns the original
   * array of products.
   */
  const filterBySearch = (optionsToFilter: Product[]) => {
    if (!searchText) return optionsToFilter
    return optionsToFilter.filter(product => {
      const productName = getTranslation(product.translations, 'fi').name.toLowerCase()
      const productCode = product.product_code.toLowerCase()
      const searchTextLower = searchText.toLowerCase()
      return productName.includes(searchTextLower) || productCode.includes(searchTextLower)
    })
  }

  const filterByProductGroup = (optionsToFilter: Product[]) => {
    if (!productGroupFilter) return optionsToFilter
    return optionsToFilter.filter(product => product.product_group === productGroupFilter)
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value
    setProductGroupFilter(newValue)
  }

  const handleSave = (rightItems: Product[]) => {
    if (!selectedCatalog) return
    const selectedProductIds = rightItems.map(product => product.id)

    updateSingleCatalog({
      id: selectedCatalog.id,
      product_ids: selectedProductIds
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Successfully saved changes.`)
        handleSaveSuccess?.()
      })
      .catch(e => {
        console.error(e)
        SnackbarUtils.error(t`An error occurred while saving changes.`)
      })
  }

  const renderItemContent = (product: Product) => {
    const labelId = `transfer-list-item-${product.id}-label`
    return (
      <ListItemText
        id={labelId}
        primary={`${getTranslation(product?.translations, 'fi').name}`}
        secondary={product?.product_code}
      />
    )
  }

  const productGroupElementId = useId()
  const isLoading =
    allTariffCatalogProductsLoading === 'pending' ||
    allTariffProductsLoading === 'pending' ||
    isUpdateLoading

  return (
    <WithLoader loading={isLoading}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <FormControl sx={{ width: '48%' }}>
          <TextField
            size="small"
            sx={{ backgroundColor: 'background.default' }}
            label={t`Product search`}
            placeholder={t`Search products`}
            value={searchText}
            onChange={handleSearchTextChange}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
          />
        </FormControl>
        <FormControl sx={{ width: '48%' }}>
          <InputLabel shrink id={productGroupElementId}>{t`Product group`}</InputLabel>
          <Select
            name="product_group"
            labelId={productGroupElementId}
            value={productGroupFilter}
            size="small"
            displayEmpty
            label={t`Product group`}
            onChange={handleChange}
            sx={{ backgroundColor: 'background.default' }}
            input={<OutlinedInput notched label={t`Product group`} />}
          >
            {productGroupOptions?.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <CustomTransferList
        leftItemsHeaderText={t`Product listing`}
        rightItemsHeaderText={t`Sales channel products`}
        leftItems={tariffProductsWithoutCatalogProducts}
        rightItems={allTariffCatalogProducts}
        renderItemContent={renderItemContent}
        handleSave={handleSave}
        handleCancel={handleCancel}
        compareFn={(item: Product) => item.id}
        filterFn={filterOptions}
      />
    </WithLoader>
  )
}

export default CatalogProductsEdit
